// @client/components/profile-manager/ProfileFilters.tsx
// V2.1 - Multi-List Support mit einfacher Checkbox-Liste (ohne cmdk)

import React, { useEffect, useState, useMemo } from "react";
import { Search, Filter, X, ChevronDown, ChevronUp, RotateCcw, Calendar, Link2, Check } from "lucide-react";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@client/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";
import { Badge } from "@client/components/ui/badge";
import { Checkbox } from "@client/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@client/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@client/components/ui/popover";
import { Calendar as CalendarComponent } from "@client/components/ui/calendar";
import { ScrollArea } from "@client/components/ui/scroll-area";
import { useToast } from "@client/hooks/use-toast";
import { format } from "date-fns";

// Deep-Link Helper
import { buildShareableLink } from "@client/utils/deep-link-helpers";

// Import Core-Types direkt via @shared/types
import type { 
  Category, 
  List, 
  User, 
  ScoreRange,
  ProfileFilters as IProfileFilters 
} from "@shared/types";

export interface ProfileFiltersProps {
  filters: IProfileFilters;
  lists: List[];
  categories: Category[];
  users: User[];
  scoreRanges: ScoreRange[];
  hasActiveFilters: boolean;
  activeFilterCount: number;
  onFilterChange: (key: keyof IProfileFilters, value: any) => void;
  onClearFilters: () => void;
  onRefresh: () => void;
  loading?: boolean;
}

export function ProfileFilters({
  filters,
  lists,
  categories,
  users,
  scoreRanges,
  hasActiveFilters,
  activeFilterCount,
  onFilterChange,
  onClearFilters,
  onRefresh,
  loading
}: ProfileFiltersProps) {

  const { toast } = useToast();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [listSearchInput, setListSearchInput] = useState('');
  const [categorySearchInput, setCategorySearchInput] = useState('');
  const [listPopoverOpen, setListPopoverOpen] = useState(false);
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // ✅ Multi-List Support: Selected List IDs
  const selectedListIds: number[] = useMemo(() => {
    if (filters.listIds && filters.listIds.length > 0) {
      return filters.listIds;
    }
    if (typeof filters.listId === 'number') {
      return [filters.listId];
    }
    return [];
  }, [filters.listId, filters.listIds]);

  // ✅ Selected Category IDs
  const selectedCategoryIds: number[] = filters.categoryIds || [];

  // ✅ Filtered lists based on search
  const filteredLists = useMemo(() => {
    if (!listSearchInput) return lists;
    return lists.filter(list => 
      list.name.toLowerCase().includes(listSearchInput.toLowerCase())
    );
  }, [lists, listSearchInput]);

  // ✅ Filtered categories based on search
  const filteredCategories = useMemo(() => {
    if (!categorySearchInput) return categories;
    return categories.filter(category => 
      category.name.toLowerCase().includes(categorySearchInput.toLowerCase())
    );
  }, [categories, categorySearchInput]);

  // ✅ List Toggle Handler
  const handleListToggle = (listId: number) => {
    const currentIds = [...selectedListIds];
    const index = currentIds.indexOf(listId);
    
    if (index === -1) {
      currentIds.push(listId);
    } else {
      currentIds.splice(index, 1);
    }
    
    onFilterChange('listIds', currentIds.length > 0 ? currentIds : undefined);
    onFilterChange('listId', undefined); // Clear legacy
  };

  // ✅ Clear all lists
  const handleClearLists = () => {
    onFilterChange('listIds', undefined);
    onFilterChange('listId', undefined);
    setListPopoverOpen(false);
  };

  // ✅ Category Toggle Handler
  const handleCategoryToggle = (categoryId: number) => {
    const currentIds = [...selectedCategoryIds];
    const index = currentIds.indexOf(categoryId);
    
    if (index === -1) {
      currentIds.push(categoryId);
    } else {
      currentIds.splice(index, 1);
    }
    
    onFilterChange('categoryIds', currentIds.length > 0 ? currentIds : undefined);
  };

  // ✅ Clear all categories
  const handleClearCategories = () => {
    onFilterChange('categoryIds', undefined);
    onFilterChange('hasCategories', undefined);
    setCategoryPopoverOpen(false);
  };

  const handleDateSelect = (field: 'afterDate' | 'beforeDate', date: Date | undefined) => {
    if (date) {
      onFilterChange(field, format(date, 'yyyy-MM-dd'));
    } else {
      onFilterChange(field, undefined);
    }
  };

  const handleCopyLink = () => {
    const url = buildShareableLink(filters);
    navigator.clipboard.writeText(url).then(() => {
      toast({
        title: 'Link copied',
        description: 'Filter link copied to clipboard',
        duration: 3000
      });
    }).catch(() => {
      toast({
        title: 'Copy failed',
        description: 'Could not copy link to clipboard',
        variant: 'destructive'
      });
    });
  };

  // ✅ Label Helpers
  const getListLabel = () => {
    if (selectedListIds.length === 0) return 'All Lists';
    if (selectedListIds.length === 1) {
      const list = lists.find(l => l.id === selectedListIds[0]);
      return list?.name || 'Selected';
    }
    return `${selectedListIds.length} Lists`;
  };

  const getCategoryLabel = () => {
    if (selectedCategoryIds.length === 0) return 'All Categories';
    if (selectedCategoryIds.length === 1) {
      const cat = categories.find(c => c.id === selectedCategoryIds[0]);
      return cat?.name || 'Selected';
    }
    return `${selectedCategoryIds.length} Categories`;
  };

  const selectedCategories = categories.filter(cat => 
    filters.categoryIds?.includes(cat.id)
  );

  const scoreRangeOptions = [
    { value: 'all', label: 'All Scores' },
    ...(scoreRanges?.map(range => ({
      value: range.id,
      label: `${range.label} (${range.minValue}-${range.maxValue})`
    })) || [])
  ];

  const timeFieldOptions = [
    { value: 'createdAt', label: 'Created At' },
    { value: 'modifiedAt', label: 'Modified At' },
    { value: 'exportedAt', label: 'Exported At' }
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary">{activeFilterCount}</Badge>
            )}
            {loading && (
              <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
            )}
          </CardTitle>
          <div className="flex gap-2">
            {hasActiveFilters && (
              <>
                <Button variant="outline" size="sm" onClick={handleCopyLink} title="Copy filter link to clipboard">
                  <Link2 className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
                <Button variant="outline" size="sm" onClick={onClearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Name, company, title, location..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onBlur={() => {
                  if (searchInput !== filters.search) {
                    onFilterChange('search', searchInput || undefined);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onFilterChange('search', searchInput || undefined);
                  }
                }}
                className="pl-10"
                disabled={loading}
              />
            </div>
          </div>

          {/* ✅ List Multi-Select Popover */}
          <div className="space-y-2">
            <label className="text-sm font-medium">List</label>
            <Popover open={listPopoverOpen} onOpenChange={setListPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={listPopoverOpen}
                  className="w-full justify-between"
                  disabled={loading}
                >
                  <span className="truncate">{getListLabel()}</span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search lists..."
                    value={listSearchInput}
                    onChange={(e) => setListSearchInput(e.target.value)}
                    className="h-8"
                  />
                </div>
                <ScrollArea className="h-[200px]">
                  <div className="p-2 space-y-1">
                    {/* Clear Selection */}
                    {selectedListIds.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearLists}
                        className="w-full justify-start text-muted-foreground h-8"
                      >
                        <X className="mr-2 h-4 w-4" />
                        Clear selection ({selectedListIds.length})
                      </Button>
                    )}
                    
                    {/* List Items */}
                    {filteredLists.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        No lists found.
                      </div>
                    ) : (
                      filteredLists.map((list) => {
                        const isSelected = selectedListIds.includes(list.id);
                        return (
                          <div
                            key={list.id}
                            onClick={() => handleListToggle(list.id)}
                            className="flex items-center gap-2 p-2 rounded-sm hover:bg-accent cursor-pointer"
                          >
                            <div className={`flex h-4 w-4 items-center justify-center rounded border ${
                              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}>
                              {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className="flex-1 truncate text-sm">{list.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {list.profileCount || 0}
                            </Badge>
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
            
            {/* Selected Lists Badges */}
            {selectedListIds.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedListIds.slice(0, 2).map(id => {
                  const list = lists.find(l => l.id === id);
                  return list ? (
                    <Badge 
                      key={id} 
                      variant="secondary" 
                      className="text-xs cursor-pointer"
                      onClick={() => handleListToggle(id)}
                    >
                      {list.name.length > 15 ? list.name.slice(0, 15) + '...' : list.name}
                      <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ) : null;
                })}
                {selectedListIds.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{selectedListIds.length - 2} more
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* ✅ Category Multi-Select Popover */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Categories</label>
            <Popover open={categoryPopoverOpen} onOpenChange={setCategoryPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={categoryPopoverOpen}
                  className="w-full justify-between"
                  disabled={loading}
                >
                  <span className="truncate">{getCategoryLabel()}</span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0" align="start">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search categories..."
                    value={categorySearchInput}
                    onChange={(e) => setCategorySearchInput(e.target.value)}
                    className="h-8"
                  />
                </div>
                <ScrollArea className="h-[200px]">
                  <div className="p-2 space-y-1">
                    {/* Clear Selection */}
                    {selectedCategoryIds.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearCategories}
                        className="w-full justify-start text-muted-foreground h-8"
                      >
                        <X className="mr-2 h-4 w-4" />
                        Clear selection ({selectedCategoryIds.length})
                      </Button>
                    )}
                    
                    {/* Category Items */}
                    {filteredCategories.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        No categories found.
                      </div>
                    ) : (
                      filteredCategories.map((category) => {
                        const isSelected = selectedCategoryIds.includes(category.id);
                        return (
                          <div
                            key={category.id}
                            onClick={() => handleCategoryToggle(category.id)}
                            className="flex items-center gap-2 p-2 rounded-sm hover:bg-accent cursor-pointer"
                          >
                            <div className={`flex h-4 w-4 items-center justify-center rounded border ${
                              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}>
                              {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            {category.color && (
                              <div 
                                className="w-3 h-3 rounded-full border"
                                style={{ backgroundColor: category.color }}
                              />
                            )}
                            <span className="flex-1 truncate text-sm">{category.name}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
            
            {/* Selected Category Badges */}
            {selectedCategoryIds.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedCategoryIds.slice(0, 2).map(id => {
                  const cat = categories.find(c => c.id === id);
                  return cat ? (
                    <Badge 
                      key={id} 
                      variant="secondary" 
                      className="text-xs cursor-pointer"
                      onClick={() => handleCategoryToggle(id)}
                      style={cat.color ? { borderColor: cat.color } : undefined}
                    >
                      {cat.name}
                      <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ) : null;
                })}
                {selectedCategoryIds.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{selectedCategoryIds.length - 2} more
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Score Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Score</label>
            <Select 
              value={filters.scoreRange || 'all'} 
              onValueChange={(value) => onFilterChange('scoreRange', value === 'all' ? undefined : value)}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Scores" />
              </SelectTrigger>
              <SelectContent>
                {scoreRangeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <Collapsible open={showAdvancedFilters} onOpenChange={setShowAdvancedFilters}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Advanced Filters
              </span>
              {showAdvancedFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="space-y-4 pt-4">
            {/* Created By */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Created By</label>
                <Select 
                  value={filters.createdBy?.toString() || 'all'} 
                  onValueChange={(value) => onFilterChange('createdBy', value === 'all' ? undefined : parseInt(value))}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users?.map(user => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.displayName || user.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">After Field</label>
                <Select 
                  value={filters.afterField || ''} 
                  onValueChange={(value) => onFilterChange('afterField', value || undefined)}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select field..." />
                  </SelectTrigger>
                  <SelectContent>
                    {timeFieldOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">After Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={
                        "w-full justify-start text-left font-normal" +
                        (!filters.afterDate ? " text-muted-foreground" : "")
                      }
                      disabled={loading || !filters.afterField}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {filters.afterDate ? new Date(filters.afterDate).toLocaleDateString() : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={filters.afterDate ? new Date(filters.afterDate) : undefined}
                      onSelect={(date) => handleDateSelect('afterDate', date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">&nbsp;</label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onFilterChange('afterField', undefined);
                    onFilterChange('afterDate', undefined);
                  }}
                  disabled={loading || (!filters.afterField && !filters.afterDate)}
                  className="w-full"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear After
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Before Field</label>
                <Select 
                  value={filters.beforeField || ''} 
                  onValueChange={(value) => onFilterChange('beforeField', value || undefined)}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select field..." />
                  </SelectTrigger>
                  <SelectContent>
                    {timeFieldOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Before Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={
                        "w-full justify-start text-left font-normal" +
                        (!filters.beforeDate ? " text-muted-foreground" : "")
                      }
                      disabled={loading || !filters.beforeField}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {filters.beforeDate ? new Date(filters.beforeDate).toLocaleDateString() : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={filters.beforeDate ? new Date(filters.beforeDate) : undefined}
                      onSelect={(date) => handleDateSelect('beforeDate', date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">&nbsp;</label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onFilterChange('beforeField', undefined);
                    onFilterChange('beforeDate', undefined);
                  }}
                  disabled={loading || (!filters.beforeField && !filters.beforeDate)}
                  className="w-full"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Before
                </Button>
              </div>
            </div>

            {/* Marker Checkboxes */}
            <div>
              <label className="text-sm font-medium mb-3 block">Marker Filters</label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isExported"
                    checked={filters.isExported || false}
                    onCheckedChange={(checked) => onFilterChange('isExported', checked || undefined)}
                    disabled={loading}
                  />
                  <label htmlFor="isExported" className="text-sm cursor-pointer">
                    Is Exported
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isActive"
                    checked={filters.isActive || false}
                    onCheckedChange={(checked) => onFilterChange('isActive', checked || undefined)}
                    disabled={loading}
                  />
                  <label htmlFor="isActive" className="text-sm cursor-pointer">
                    Is Active
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isBlocked"
                    checked={filters.isBlocked || false}
                    onCheckedChange={(checked) => onFilterChange('isBlocked', checked || undefined)}
                    disabled={loading}
                  />
                  <label htmlFor="isBlocked" className="text-sm cursor-pointer">
                    Is Blocked
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isRegistered"
                    checked={filters.isRegistered || false}
                    onCheckedChange={(checked) => onFilterChange('isRegistered', checked || undefined)}
                    disabled={loading}
                  />
                  <label htmlFor="isRegistered" className="text-sm cursor-pointer">
                    Is Registered
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hasCategories"
                    checked={filters.hasCategories || false}
                    onCheckedChange={(checked) => onFilterChange('hasCategories', checked || undefined)}
                    disabled={loading}
                  />
                  <label htmlFor="hasCategories" className="text-sm cursor-pointer">
                    Has Categories
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hasLists"
                    checked={filters.hasLists || false}
                    onCheckedChange={(checked) => onFilterChange('hasLists', checked || undefined)}
                    disabled={loading}
                  />
                  <label htmlFor="hasLists" className="text-sm cursor-pointer">
                    Has Lists
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hasEmail"
                    checked={filters.hasEmail || false}
                    onCheckedChange={(checked) => onFilterChange('hasEmail', checked || undefined)}
                    disabled={loading}
                  />
                  <label htmlFor="hasEmail" className="text-sm cursor-pointer">
                    Has Email
                  </label>
                </div>
              </div>
            </div>

            {/* Filter Summary */}
            {hasActiveFilters && (
              <div className="pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  <strong>Active Filters:</strong>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {filters.search && (
                      <Badge variant="secondary">Search: "{filters.search}"</Badge>
                    )}
                    {selectedListIds.length > 0 && (
                      <Badge variant="secondary">
                        Lists: {selectedListIds.length} selected
                      </Badge>
                    )}
                    {selectedCategories.length > 0 && (
                      <Badge variant="secondary">
                        Categories: {selectedCategories.length} selected
                      </Badge>
                    )}
                    {filters.hasCategories === false && (
                      <Badge variant="secondary">No Categories</Badge>
                    )}
                    {filters.scoreRange && filters.scoreRange !== 'all' && (
                      <Badge variant="secondary">
                        Score: {scoreRangeOptions.find(r => r.value.toString() === filters.scoreRange)?.label}
                      </Badge>
                    )}
                    {filters.afterField && filters.afterDate && (
                      <Badge variant="secondary">
                        After: {filters.afterField} &gt; {new Date(filters.afterDate).toLocaleDateString()}
                      </Badge>
                    )}
                    {filters.beforeField && filters.beforeDate && (
                      <Badge variant="secondary">
                        Before: {filters.beforeField} &lt; {new Date(filters.beforeDate).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}